import React, { useEffect, useMemo, useState } from "react";
import { Dialog } from "@mui/material";
import { useTheme, themes } from "../ThemeContext";
import { usePermissions } from "../permissions/PermissionsProvider";
import {
  ACTIONS,
  PERMISSIONS_DOC_PATH,
  DEFAULT_PERMISSION_CONFIG,
} from "../permissions/definition";
import { db } from "../firebaseConfig";
import { doc, getDoc, setDoc } from "firebase/firestore";

const SettingsDialog = ({ open, onClose }) => {
  const { currentTheme, changeTheme, theme } = useTheme();
  const { can } = usePermissions();
  const canManagePermissions = can(ACTIONS.SETTINGS_MANAGE_PERMISSIONS);
  const canViewInvoices = can(ACTIONS.INVOICES_CREATE) || canManagePermissions;

  const [section, setSection] = useState("personalization");
  const [permConfig, setPermConfig] = useState(DEFAULT_PERMISSION_CONFIG);
  const [loadingPerms, setLoadingPerms] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [showAddUserPopup, setShowAddUserPopup] = useState(false);
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [invoicingConfig, setInvoicingConfig] = useState(null);
  const [invoicingDraft, setInvoicingDraft] = useState(null);
  const [loadingInvoicing, setLoadingInvoicing] = useState(false);
  const [invoicingError, setInvoicingError] = useState("");
  const [invoicingSaving, setInvoicingSaving] = useState(false);
  const [invoicingSaveError, setInvoicingSaveError] = useState("");
  const [invoicingSaveSuccess, setInvoicingSaveSuccess] = useState("");
  const [selectedProfileKey, setSelectedProfileKey] = useState("");

  const rolesList = useMemo(
    () => Object.keys(permConfig.roles || {}),
    [permConfig]
  );
  const sortedUsers = useMemo(
    () =>
      Object.entries(permConfig.users || {}).sort(([a], [b]) =>
        a.localeCompare(b)
      ),
    [permConfig.users]
  );
  const permissionSections = useMemo(
    () => [
      {
        title: "Viewing",
        actions: [
          ACTIONS.NAV_VIEW_PROJECTS,
          ACTIONS.NAV_VIEW_TASKS,
          ACTIONS.NAV_VIEW_SCHEDULE,
          ACTIONS.NAV_VIEW_CLIENTS,
          ACTIONS.NAV_VIEW_ANALYTICS,
          ACTIONS.PROJECT_VIEW_DETAIL,
          ACTIONS.FINANCIALS_VIEW,
          ACTIONS.TASKS_FILTER_ALL_ANALYSTS,
        ],
      },
      {
        title: "Creating & Editing",
        actions: [
          ACTIONS.PROJECTS_CREATE,
          ACTIONS.PROJECTS_EDIT,
          ACTIONS.PROJECTS_DELETE,
          ACTIONS.CLIENTS_EDIT,
          ACTIONS.INVOICES_CREATE,
          ACTIONS.PROJECTS_EXPORT,
          ACTIONS.SCHEDULE_MANAGE_ALLOCATIONS,
        ],
      },
      {
        title: "Management",
        actions: [ACTIONS.SETTINGS_MANAGE_PERMISSIONS],
      },
    ],
    []
  );

  useEffect(() => {
    if (sortedUsers.length === 0) {
      setSelectedUser("");
      return;
    }
    const first = sortedUsers[0][0];
    if (!selectedUser || !permConfig.users?.[selectedUser]) {
      setSelectedUser(first);
    }
  }, [sortedUsers, permConfig.users, selectedUser]);

  // Close dropdowns when selected user changes
  useEffect(() => {
    setShowRoleDropdown(false);
  }, [selectedUser]);

  // Close role dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showRoleDropdown && !e.target.closest("[data-role-dropdown]")) {
        setShowRoleDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showRoleDropdown]);

  // Human-readable permission labels
  const permissionLabels = {
    [ACTIONS.NAV_VIEW_PROJECTS]: "View projects",
    [ACTIONS.PROJECT_VIEW_DETAIL]: "View project detail page",
    [ACTIONS.NAV_VIEW_TASKS]: "View tasks",
    [ACTIONS.NAV_VIEW_SCHEDULE]: "View schedule",
    [ACTIONS.NAV_VIEW_CLIENTS]: "View clients",
    [ACTIONS.NAV_VIEW_ANALYTICS]: "View analytics",
    [ACTIONS.FINANCIALS_VIEW]: "View financials",
    [ACTIONS.TASKS_FILTER_ALL_ANALYSTS]: "Filter all analysts' tasks",
    [ACTIONS.PROJECTS_CREATE]: "Create projects",
    [ACTIONS.PROJECTS_EDIT]: "Edit projects",
    [ACTIONS.PROJECTS_DELETE]: "Delete projects",
    [ACTIONS.CLIENTS_EDIT]: "Edit clients",
    [ACTIONS.INVOICES_CREATE]: "Create invoices",
    [ACTIONS.PROJECTS_EXPORT]: "Export projects",
    [ACTIONS.SCHEDULE_MANAGE_ALLOCATIONS]: "Manage schedule allocations",
    [ACTIONS.SETTINGS_MANAGE_PERMISSIONS]: "Manage permissions",
  };

  const loadPermissions = async () => {
    if (!canManagePermissions) return;
    setLoadingPerms(true);
    setLoadError("");
    setSaveError("");
    setSaveSuccess("");
    try {
      const ref = doc(
        db,
        PERMISSIONS_DOC_PATH.collection,
        PERMISSIONS_DOC_PATH.doc
      );
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data() || {};
        setPermConfig({
          defaultRole:
            data.defaultRole || DEFAULT_PERMISSION_CONFIG.defaultRole,
          roles: data.roles || DEFAULT_PERMISSION_CONFIG.roles,
          users: data.users || {},
        });
      } else {
        setPermConfig(DEFAULT_PERMISSION_CONFIG);
      }
    } catch (err) {
      setLoadError(err?.message || "Failed to load permissions");
    } finally {
      setLoadingPerms(false);
    }
  };

  useEffect(() => {
    if (open && canManagePermissions) {
      loadPermissions();
    }
  }, [open, canManagePermissions]);

  const loadInvoicingConfig = async () => {
    if (!canViewInvoices) return;
    setLoadingInvoicing(true);
    setInvoicingError("");
    setInvoicingSaveError("");
    setInvoicingSaveSuccess("");
    try {
      const ref = doc(db, "appConfig", "invoicing");
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        setInvoicingConfig(data);
        setInvoicingDraft(data);
        const keys = Object.keys(data.profiles || {});
        if (keys.length) {
          setSelectedProfileKey((prev) =>
            prev && keys.includes(prev) ? prev : keys[0]
          );
        }
      } else {
        setInvoicingError("Invoicing config not found");
      }
    } catch (err) {
      setInvoicingError(
        err?.message || "Failed to load invoicing configuration"
      );
    } finally {
      setLoadingInvoicing(false);
    }
  };

  useEffect(() => {
    if (open && section === "invoicing" && canViewInvoices) {
      loadInvoicingConfig();
    }
  }, [open, section, canViewInvoices]);

  const handleInvoicingFieldChange = (profileKey, path, value) => {
    setInvoicingDraft((prev) => {
      if (!prev) return prev;
      const next = { ...prev, profiles: { ...(prev.profiles || {}) } };
      const profile = { ...(next.profiles[profileKey] || {}) };
      const [group, field] = path.split(".");
      profile[group] = { ...(profile[group] || {}), [field]: value };
      next.profiles[profileKey] = profile;
      return next;
    });
  };

  const handleInvoicingListChange = (profileKey, group, field, value) => {
    const lines = value
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    setInvoicingDraft((prev) => {
      if (!prev) return prev;
      const next = { ...prev, profiles: { ...(prev.profiles || {}) } };
      const profile = { ...(next.profiles[profileKey] || {}) };
      profile[group] = { ...(profile[group] || {}), [field]: lines };
      next.profiles[profileKey] = profile;
      return next;
    });
  };

  const validateInvoicingDraft = () => {
    if (!invoicingDraft?.profiles) return "No invoicing configuration loaded.";
    for (const [key, profile] of Object.entries(invoicingDraft.profiles)) {
      if (!profile.company?.name) return `${key}: Company name is required.`;
      if (!profile.payment?.accountHolder)
        return `${key}: Account holder is required.`;
    }
    return "";
  };

  const saveInvoicingConfig = async () => {
    if (!canViewInvoices || !invoicingDraft) return;
    const validationError = validateInvoicingDraft();
    if (validationError) {
      setInvoicingSaveError(validationError);
      setInvoicingSaveSuccess("");
      return;
    }
    setInvoicingSaving(true);
    setInvoicingSaveError("");
    setInvoicingSaveSuccess("");
    try {
      const payload = {
        version: invoicingDraft.version || 1,
        profiles: invoicingDraft.profiles || {},
      };
      const ref = doc(db, "appConfig", "invoicing");
      await setDoc(ref, payload, { merge: false });
      setInvoicingSaveSuccess("Invoicing settings updated");
      setInvoicingConfig(payload);
    } catch (err) {
      setInvoicingSaveError(
        err?.message || "Failed to save invoicing configuration"
      );
    } finally {
      setInvoicingSaving(false);
    }
  };

  const handleAddUser = (email) => {
    const normalized = (email || "").trim().toLowerCase();
    if (!normalized) return;
    setPermConfig((prev) => ({
      ...prev,
      users: {
        ...prev.users,
        [normalized]: prev.users?.[normalized] || {
          roles: [prev.defaultRole],
          overrides: {},
        },
      },
    }));
    setSelectedUser(normalized);
  };

  const handleRemoveUser = (email) => {
    setPermConfig((prev) => {
      const nextUsers = { ...(prev.users || {}) };
      delete nextUsers[email];
      return { ...prev, users: nextUsers };
    });
    if (selectedUser === email) {
      setSelectedUser("");
    }
  };

  const handleRolesChange = (email, role) => {
    const normalizedRole = role || permConfig.defaultRole;
    setPermConfig((prev) => ({
      ...prev,
      users: {
        ...prev.users,
        [email]: {
          ...(prev.users?.[email] || {}),
          roles: [normalizedRole],
          overrides: prev.users?.[email]?.overrides || {},
        },
      },
    }));
  };

  const handlePermissionCheckbox = (email, actionId, checked) => {
    setPermConfig((prev) => {
      const user = prev.users?.[email] || {
        roles: [prev.defaultRole],
        overrides: {},
      };
      const nextOverrides = { ...(user.overrides || {}) };
      nextOverrides[actionId] = checked === true;
      return {
        ...prev,
        users: {
          ...prev.users,
          [email]: { ...user, overrides: nextOverrides },
        },
      };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError("");
    setSaveSuccess("");
    try {
      const payload = {
        defaultRole:
          permConfig.defaultRole || DEFAULT_PERMISSION_CONFIG.defaultRole,
        roles: permConfig.roles || DEFAULT_PERMISSION_CONFIG.roles,
        users: permConfig.users || {},
      };
      const ref = doc(
        db,
        PERMISSIONS_DOC_PATH.collection,
        PERMISSIONS_DOC_PATH.doc
      );
      await setDoc(ref, payload, { merge: false });
      setSaveSuccess("Permissions updated");
    } catch (err) {
      setSaveError(err?.message || "Failed to save permissions");
    } finally {
      setSaving(false);
    }
  };

  // Icons as inline SVGs
  const PaletteIcon = () => (
    <svg
      className="w-7 h-7"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4.098 19.902a3.75 3.75 0 005.304 0l6.401-6.402M6.75 21A3.75 3.75 0 013 17.25V4.125C3 3.504 3.504 3 4.125 3h5.25c.621 0 1.125.504 1.125 1.125v4.072M6.75 21a3.75 3.75 0 003.75-3.75V8.197M6.75 21h13.125c.621 0 1.125-.504 1.125-1.125v-5.25c0-.621-.504-1.125-1.125-1.125h-4.072M10.5 8.197l2.88-2.88c.438-.439 1.15-.439 1.59 0l3.712 3.713c.44.44.44 1.152 0 1.59l-2.879 2.88M6.75 17.25h.008v.008H6.75v-.008z"
      />
    </svg>
  );

  const ShieldIcon = () => (
    <svg
      className="w-7 h-7"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
      />
    </svg>
  );

  const CloseIcon = () => (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );

  const UserPlusIcon = () => (
    <svg
      className="w-4 h-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z"
      />
    </svg>
  );

  const TrashIcon = () => (
    <svg
      className="w-4 h-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
      />
    </svg>
  );

  const CheckIcon = () => (
    <svg
      className="w-4 h-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4.5 12.75l6 6 9-13.5"
      />
    </svg>
  );

  const RefreshIcon = () => (
    <svg
      className="w-4 h-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
      />
    </svg>
  );

  const Spinner = () => (
    <svg
      className="animate-spin w-5 h-5 text-gray-400"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          padding: 0,
          overflow: "hidden",
        },
      }}
    >
      <div className="flex flex-col h-[710px]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Settings</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Side Navigation */}
          <div className="w-56 bg-gray-50/50 border-r border-gray-100 p-3 flex flex-col gap-1">
            <button
              onClick={() => setSection("personalization")}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-200 ${
                section === "personalization"
                  ? "bg-white shadow-sm text-gray-900 ring-1 ring-gray-200"
                  : "text-gray-600 hover:bg-white/60 hover:text-gray-900"
              }`}
            >
              <span
                style={{
                  color:
                    section === "personalization"
                      ? theme.filterAccent
                      : "#9ca3af",
                }}
              >
                <PaletteIcon />
              </span>
              <span className="font-medium text-base">Personalization</span>
            </button>
            <button
              onClick={() => canManagePermissions && setSection("permissions")}
              disabled={!canManagePermissions}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-200 ${
                !canManagePermissions
                  ? "text-gray-300 cursor-not-allowed"
                  : section === "permissions"
                  ? "bg-white shadow-sm text-gray-900 ring-1 ring-gray-200"
                  : "text-gray-600 hover:bg-white/60 hover:text-gray-900"
              }`}
            >
              <span
                style={{
                  color: !canManagePermissions
                    ? "#d1d5db"
                    : section === "permissions"
                    ? theme.filterAccent
                    : "#9ca3af",
                }}
              >
                <ShieldIcon />
              </span>
              <span className="font-medium text-base">Permissions</span>
            </button>
            <button
              onClick={() => canViewInvoices && setSection("invoicing")}
              disabled={!canViewInvoices}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-200 ${
                !canViewInvoices
                  ? "text-gray-300 cursor-not-allowed"
                  : section === "invoicing"
                  ? "bg-white shadow-sm text-gray-900 ring-1 ring-gray-200"
                  : "text-gray-600 hover:bg-white/60 hover:text-gray-900"
              }`}
            >
              <span
                style={{
                  color: !canViewInvoices
                    ? "#d1d5db"
                    : section === "invoicing"
                    ? theme.filterAccent
                    : "#9ca3af",
                }}
              >
                <svg
                  className="w-7 h-7"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                  />
                </svg>
              </span>
              <span className="font-medium text-base">Invoicing</span>
            </button>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Personalization Section - kept as is */}
            {section === "personalization" && (
              <div className="mb-3">
                <h3 className="text-base font-semibold text-gray-900 mb-2">
                  Personalization
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Choose a theme for the application. This will affect the login
                  screen, toolbar buttons, and form headers.
                </p>

                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(themes).map(([key, themeOption]) => (
                    <button
                      key={key}
                      onClick={() => changeTheme(key)}
                      className="relative p-4 rounded-xl border-2 text-left transition-all duration-200"
                      style={
                        currentTheme === key
                          ? {
                              borderColor: theme.filterAccent,
                              backgroundColor: theme.filterSelectedBg,
                            }
                          : { borderColor: "#e5e7eb" }
                      }
                      onMouseEnter={(e) => {
                        if (currentTheme !== key) {
                          e.currentTarget.style.borderColor = "#d1d5db";
                          e.currentTarget.style.backgroundColor = "#f9fafb";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (currentTheme !== key) {
                          e.currentTarget.style.borderColor = "#e5e7eb";
                          e.currentTarget.style.backgroundColor = "transparent";
                        }
                      }}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div
                          className="w-8 h-8 rounded-full shadow-sm"
                          style={{ background: themeOption.loginBackground }}
                        />
                        <span
                          className="font-medium"
                          style={{
                            color:
                              currentTheme === key
                                ? theme.filterSelectedText
                                : "#374151",
                          }}
                        >
                          {themeOption.name}
                        </span>
                      </div>
                      {currentTheme === key && (
                        <div
                          className="absolute top-4 right-4"
                          style={{ color: theme.filterAccent }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="w-5 h-5"
                          >
                            <path
                              fillRule="evenodd"
                              d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Permissions Section - Redesigned */}
            {section === "permissions" && (
              <div className="flex flex-col h-full">
                {!canManagePermissions ? (
                  <div className="flex items-center gap-3 px-4 py-3 bg-blue-50 text-blue-700 rounded-lg text-sm">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    You do not have permission to manage user access.
                  </div>
                ) : (
                  <>
                    {/* Alerts */}
                    {loadError && (
                      <div className="flex items-center gap-2 px-4 py-3 mb-4 bg-red-50 text-red-700 rounded-lg text-sm">
                        <svg
                          className="w-4 h-4 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {loadError}
                      </div>
                    )}
                    {saveError && (
                      <div className="flex items-center gap-2 px-4 py-3 mb-4 bg-red-50 text-red-700 rounded-lg text-sm">
                        <svg
                          className="w-4 h-4 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {saveError}
                      </div>
                    )}
                    {saveSuccess && (
                      <div className="flex items-center gap-2 px-4 py-3 mb-4 bg-green-50 text-green-700 rounded-lg text-sm">
                        <svg
                          className="w-4 h-4 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {saveSuccess}
                      </div>
                    )}

                    {loadingPerms ? (
                      <div className="flex-1 flex items-center justify-center">
                        <Spinner />
                      </div>
                    ) : (
                      <div className="flex flex-col flex-1 min-h-0">
                        {/* Users Horizontal Tabs */}
                        <div className="flex items-center gap-2 pb-4 border-b border-gray-100 flex-wrap">
                          {sortedUsers.map(([email]) => {
                            const displayName =
                              email.split("@")[0].charAt(0).toUpperCase() +
                              email.split("@")[0].slice(1);
                            return (
                              <button
                                key={email}
                                onClick={() => setSelectedUser(email)}
                                className="px-4 py-2 rounded-full text-sm font-medium transition-all"
                                style={
                                  selectedUser === email
                                    ? {
                                        backgroundColor: theme.filterAccent,
                                        color: "white",
                                        boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                      }
                                    : {
                                        backgroundColor: "#f3f4f6",
                                        color: "#4b5563",
                                      }
                                }
                                onMouseEnter={(e) => {
                                  if (selectedUser !== email)
                                    e.target.style.backgroundColor = "#e5e7eb";
                                }}
                                onMouseLeave={(e) => {
                                  if (selectedUser !== email)
                                    e.target.style.backgroundColor = "#f3f4f6";
                                }}
                              >
                                {displayName}
                              </button>
                            );
                          })}
                          {/* Add User Button */}
                          <div className="relative ml-auto">
                            <button
                              onClick={() =>
                                setShowAddUserPopup(!showAddUserPopup)
                              }
                              className="p-2 rounded-full text-white transition-colors"
                              style={{ backgroundColor: theme.filterAccent }}
                              onMouseEnter={(e) =>
                                (e.target.style.backgroundColor =
                                  theme.filterAccentHover)
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.backgroundColor =
                                  theme.filterAccent)
                              }
                            >
                              <UserPlusIcon />
                            </button>
                            {/* Add User Popup */}
                            {showAddUserPopup && (
                              <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-lg border border-gray-200 p-4 z-10 w-72">
                                <h4 className="text-sm font-medium text-gray-900 mb-3">
                                  Add New User
                                </h4>
                                <input
                                  type="email"
                                  placeholder="Enter email address..."
                                  value={newUserEmail}
                                  onChange={(e) =>
                                    setNewUserEmail(e.target.value)
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      handleAddUser(newUserEmail);
                                      setNewUserEmail("");
                                      setShowAddUserPopup(false);
                                    }
                                    if (e.key === "Escape") {
                                      setShowAddUserPopup(false);
                                    }
                                  }}
                                  autoFocus
                                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-20 transition-all mb-3"
                                  style={{
                                    borderColor: theme.filterAccent + "40",
                                    boxShadow: `0 0 0 3px ${theme.filterAccent}20`,
                                  }}
                                />
                                <div className="flex gap-2 justify-end">
                                  <button
                                    onClick={() => {
                                      setShowAddUserPopup(false);
                                      setNewUserEmail("");
                                    }}
                                    className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={() => {
                                      handleAddUser(newUserEmail);
                                      setNewUserEmail("");
                                      setShowAddUserPopup(false);
                                    }}
                                    className="px-3 py-1.5 text-sm font-medium text-white rounded-lg transition-colors"
                                    style={{
                                      backgroundColor: theme.filterAccent,
                                    }}
                                  >
                                    Add User
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* User Permissions Panel */}
                        <div className="flex-1 flex flex-col min-h-0 pt-4">
                          {selectedUser && permConfig.users?.[selectedUser] ? (
                            <>
                              {/* User Header with Role Dropdown */}
                              <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                  <div
                                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium"
                                    style={{
                                      background: theme.clientAvatarGradient,
                                    }}
                                  >
                                    {selectedUser.charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <h4 className="font-medium text-gray-900 text-lg">
                                      {selectedUser
                                        .split("@")[0]
                                        .charAt(0)
                                        .toUpperCase() +
                                        selectedUser.split("@")[0].slice(1)}
                                    </h4>
                                    <p className="text-xs text-gray-500">
                                      {selectedUser}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {/* Role Dropdown */}
                                  <div className="relative" data-role-dropdown>
                                    <button
                                      onClick={() =>
                                        setShowRoleDropdown(!showRoleDropdown)
                                      }
                                      className="flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-full text-xs font-semibold transition-all border-2 hover:shadow-sm"
                                      style={{
                                        backgroundColor: theme.filterSelectedBg,
                                        color: theme.filterSelectedText,
                                        borderColor: theme.filterAccent + "40",
                                      }}
                                    >
                                      <svg
                                        className="w-3.5 h-3.5 opacity-70"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth={2}
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
                                        />
                                      </svg>
                                      {(
                                        (permConfig.users?.[selectedUser]
                                          ?.roles || [
                                          permConfig.defaultRole,
                                        ])[0] || ""
                                      )
                                        .charAt(0)
                                        .toUpperCase() +
                                        (
                                          (permConfig.users?.[selectedUser]
                                            ?.roles || [
                                            permConfig.defaultRole,
                                          ])[0] || ""
                                        ).slice(1)}
                                      <svg
                                        className={`w-3.5 h-3.5 opacity-60 transition-transform ${
                                          showRoleDropdown ? "rotate-180" : ""
                                        }`}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth={2.5}
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          d="M19 9l-7 7-7-7"
                                        />
                                      </svg>
                                    </button>
                                    {showRoleDropdown && (
                                      <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 bg-white rounded-xl shadow-xl border border-gray-100 py-1.5 z-10 min-w-[140px] overflow-hidden">
                                        {rolesList.map((role) => {
                                          const isActive = (
                                            permConfig.users?.[selectedUser]
                                              ?.roles || [
                                              permConfig.defaultRole,
                                            ]
                                          ).includes(role);
                                          return (
                                            <button
                                              key={role}
                                              onClick={() => {
                                                handleRolesChange(
                                                  selectedUser,
                                                  role
                                                );
                                                setShowRoleDropdown(false);
                                              }}
                                              className="w-full px-4 py-2.5 text-left text-sm transition-colors flex items-center justify-between"
                                              style={
                                                isActive
                                                  ? {
                                                      backgroundColor:
                                                        theme.filterSelectedBg,
                                                      color:
                                                        theme.filterSelectedText,
                                                      fontWeight: 600,
                                                    }
                                                  : { color: "#4b5563" }
                                              }
                                              onMouseEnter={(e) => {
                                                if (!isActive)
                                                  e.currentTarget.style.backgroundColor =
                                                    "#f9fafb";
                                              }}
                                              onMouseLeave={(e) => {
                                                if (!isActive)
                                                  e.currentTarget.style.backgroundColor =
                                                    "transparent";
                                              }}
                                            >
                                              {role.charAt(0).toUpperCase() +
                                                role.slice(1)}
                                              {isActive && (
                                                <svg
                                                  className="w-4 h-4"
                                                  style={{
                                                    color: theme.filterAccent,
                                                  }}
                                                  fill="none"
                                                  viewBox="0 0 24 24"
                                                  stroke="currentColor"
                                                  strokeWidth={2.5}
                                                >
                                                  <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M5 13l4 4L19 7"
                                                  />
                                                </svg>
                                              )}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                  <button
                                    onClick={() =>
                                      handleRemoveUser(selectedUser)
                                    }
                                    className="p-2 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Remove user"
                                  >
                                    <TrashIcon />
                                  </button>
                                </div>
                              </div>

                              {/* Permissions Grid */}
                              <div className="flex-1 overflow-y-auto pr-1">
                                <label className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3 block">
                                  Permissions
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                  {permissionSections.map((sect) => {
                                    const userCfg =
                                      permConfig.users?.[selectedUser] || {};
                                    const userRoles = userCfg.roles?.length
                                      ? userCfg.roles
                                      : [permConfig.defaultRole];
                                    const roleActions = new Set(
                                      userRoles.flatMap(
                                        (r) => permConfig.roles?.[r] || []
                                      )
                                    );

                                    return (
                                      <div
                                        key={sect.title}
                                        className="bg-gray-50/80 rounded-xl p-3"
                                      >
                                        <h5 className="text-sm font-semibold text-gray-700 mb-2">
                                          {sect.title}
                                        </h5>
                                        <div className="space-y-1">
                                          {sect.actions.map((actionId) => {
                                            const overrideState =
                                              userCfg.overrides?.[actionId];
                                            const effectiveAllowed =
                                              overrideState === true ||
                                              (overrideState !== false &&
                                                roleActions.has(actionId));

                                            return (
                                              <label
                                                key={`${selectedUser}-${actionId}`}
                                                className="flex items-center gap-2.5 py-1.5 cursor-pointer group"
                                                onClick={() =>
                                                  handlePermissionCheckbox(
                                                    selectedUser,
                                                    actionId,
                                                    !effectiveAllowed
                                                  )
                                                }
                                              >
                                                <div
                                                  className="relative w-9 h-5 rounded-full transition-all duration-200 cursor-pointer flex-shrink-0"
                                                  style={{
                                                    backgroundColor:
                                                      effectiveAllowed
                                                        ? theme.filterCheckboxChecked
                                                        : "#e5e7eb",
                                                  }}
                                                >
                                                  <div
                                                    className="absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-all duration-200"
                                                    style={{
                                                      left: effectiveAllowed
                                                        ? "calc(100% - 18px)"
                                                        : "2px",
                                                    }}
                                                  />
                                                </div>
                                                <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors select-none">
                                                  {permissionLabels[actionId] ||
                                                    actionId}
                                                </span>
                                              </label>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                              <svg
                                className="w-12 h-12 mb-3"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={1}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                                />
                              </svg>
                              <p className="text-sm">
                                Select a user to manage permissions
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Footer Actions */}
                    <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-gray-100">
                      <button
                        onClick={loadPermissions}
                        disabled={loadingPerms || saving}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                      >
                        <RefreshIcon />
                        Refresh
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 shadow-sm"
                        style={{ backgroundColor: theme.filterAccent }}
                        onMouseEnter={(e) =>
                          (e.target.style.backgroundColor =
                            theme.filterAccentHover)
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.backgroundColor = theme.filterAccent)
                        }
                      >
                        {saving ? (
                          <svg
                            className="animate-spin w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                        ) : (
                          <CheckIcon />
                        )}
                        Save changes
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {section === "invoicing" && (
              <div className="flex flex-col h-full">
                {!canViewInvoices ? (
                  <div className="flex items-center gap-3 px-4 py-3 bg-blue-50 text-blue-700 rounded-lg text-sm">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    You do not have permission to view invoicing settings.
                  </div>
                ) : (
                  <>
                    {/* Alerts */}
                    {invoicingError && (
                      <div className="flex items-center gap-2 px-4 py-3 mb-4 bg-red-50 text-red-700 rounded-lg text-sm">
                        <svg
                          className="w-4 h-4 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {invoicingError}
                      </div>
                    )}
                    {invoicingSaveError && (
                      <div className="flex items-center gap-2 px-4 py-3 mb-4 bg-red-50 text-red-700 rounded-lg text-sm">
                        <svg
                          className="w-4 h-4 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {invoicingSaveError}
                      </div>
                    )}
                    {invoicingSaveSuccess && (
                      <div className="flex items-center gap-2 px-4 py-3 mb-4 bg-green-50 text-green-700 rounded-lg text-sm">
                        <svg
                          className="w-4 h-4 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {invoicingSaveSuccess}
                      </div>
                    )}

                    {loadingInvoicing ? (
                      <div className="flex-1 flex items-center justify-center">
                        <Spinner />
                      </div>
                    ) : invoicingDraft ? (
                      <div className="flex flex-col flex-1 min-h-0">
                        {/* Profile Tabs - styled like user tabs in Permissions */}
                        <div className="flex items-center gap-2 pb-4 border-b border-gray-100 flex-wrap">
                          {Object.keys(invoicingDraft.profiles || {}).map(
                            (key) => (
                              <button
                                key={key}
                                onClick={() => setSelectedProfileKey(key)}
                                className="px-4 py-2 rounded-full text-sm font-medium transition-all"
                                style={
                                  selectedProfileKey === key
                                    ? {
                                        backgroundColor: theme.filterAccent,
                                        color: "white",
                                        boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                                      }
                                    : {
                                        backgroundColor: "#f3f4f6",
                                        color: "#4b5563",
                                      }
                                }
                                onMouseEnter={(e) => {
                                  if (selectedProfileKey !== key)
                                    e.target.style.backgroundColor = "#e5e7eb";
                                }}
                                onMouseLeave={(e) => {
                                  if (selectedProfileKey !== key)
                                    e.target.style.backgroundColor = "#f3f4f6";
                                }}
                              >
                                {key}
                              </button>
                            )
                          )}
                        </div>

                        {/* Profile Content */}
                        <div className="flex-1 flex flex-col min-h-0 pt-4">
                          {selectedProfileKey &&
                          invoicingDraft.profiles?.[selectedProfileKey] ? (
                            <>
                              {/* Profile Header */}
                              <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                  <div
                                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium"
                                    style={{
                                      background: theme.clientAvatarGradient,
                                    }}
                                  >
                                    <svg
                                      className="w-5 h-5"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                      strokeWidth={2}
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                      />
                                    </svg>
                                  </div>
                                  <div>
                                    <h4 className="font-medium text-gray-900 text-lg">
                                      {selectedProfileKey}
                                    </h4>
                                    <p className="text-xs text-gray-500">
                                      Invoice profile configuration
                                    </p>
                                  </div>
                                </div>
                              </div>

                              {/* Form Sections */}
                              <div className="flex-1 overflow-y-auto pr-1">
                                {(() => {
                                  const profile =
                                    invoicingDraft.profiles[selectedProfileKey];
                                  return (
                                    <div className="space-y-4">
                                      {/* Company Section */}
                                      <div className="bg-gray-50/80 rounded-xl p-4">
                                        <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                          <svg
                                            className="w-4 h-4 opacity-60"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth={2}
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                            />
                                          </svg>
                                          Company Details
                                        </h5>
                                        <div className="grid grid-cols-2 gap-3">
                                          <div className="col-span-2">
                                            <label className="block text-xs font-medium text-gray-500 mb-1">
                                              Company Name
                                            </label>
                                            <input
                                              type="text"
                                              value={
                                                profile.company?.name || ""
                                              }
                                              onChange={(e) =>
                                                handleInvoicingFieldChange(
                                                  selectedProfileKey,
                                                  "company.name",
                                                  e.target.value
                                                )
                                              }
                                              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-opacity-20 transition-all"
                                              style={{
                                                focusRingColor:
                                                  theme.filterAccent,
                                              }}
                                            />
                                          </div>
                                          <div className="col-span-2">
                                            <label className="block text-xs font-medium text-gray-500 mb-1">
                                              Address (one line per row)
                                            </label>
                                            <textarea
                                              value={(
                                                profile.company?.addressLines ||
                                                []
                                              ).join("\n")}
                                              onChange={(e) =>
                                                handleInvoicingListChange(
                                                  selectedProfileKey,
                                                  "company",
                                                  "addressLines",
                                                  e.target.value
                                                )
                                              }
                                              rows={2}
                                              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-opacity-20 transition-all resize-none"
                                            />
                                          </div>
                                          <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1">
                                              Phone
                                            </label>
                                            <input
                                              type="text"
                                              value={
                                                profile.company?.phone || ""
                                              }
                                              onChange={(e) =>
                                                handleInvoicingFieldChange(
                                                  selectedProfileKey,
                                                  "company.phone",
                                                  e.target.value
                                                )
                                              }
                                              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-opacity-20 transition-all"
                                            />
                                          </div>
                                          <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1">
                                              Email
                                            </label>
                                            <input
                                              type="email"
                                              value={
                                                profile.company?.email || ""
                                              }
                                              onChange={(e) =>
                                                handleInvoicingFieldChange(
                                                  selectedProfileKey,
                                                  "company.email",
                                                  e.target.value
                                                )
                                              }
                                              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-opacity-20 transition-all"
                                            />
                                          </div>
                                          <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1">
                                              TRN
                                            </label>
                                            <input
                                              type="text"
                                              value={profile.company?.trn || ""}
                                              onChange={(e) =>
                                                handleInvoicingFieldChange(
                                                  selectedProfileKey,
                                                  "company.trn",
                                                  e.target.value
                                                )
                                              }
                                              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-opacity-20 transition-all"
                                            />
                                          </div>
                                        </div>
                                      </div>

                                      {/* Header & Labels Section */}
                                      <div className="bg-gray-50/80 rounded-xl p-4">
                                        <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                          <svg
                                            className="w-4 h-4 opacity-60"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth={2}
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                            />
                                          </svg>
                                          Header & Branding
                                        </h5>
                                        <div className="grid grid-cols-2 gap-3">
                                          <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1">
                                              Logo URL
                                            </label>
                                            <input
                                              type="text"
                                              value={
                                                profile.header?.logoUrl || ""
                                              }
                                              onChange={(e) =>
                                                handleInvoicingFieldChange(
                                                  selectedProfileKey,
                                                  "header.logoUrl",
                                                  e.target.value
                                                )
                                              }
                                              placeholder="https://..."
                                              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-opacity-20 transition-all"
                                            />
                                          </div>
                                          <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1">
                                              Logo URL (Tax Invoice)
                                            </label>
                                            <input
                                              type="text"
                                              value={
                                                profile.header?.logoUrlTax || ""
                                              }
                                              onChange={(e) =>
                                                handleInvoicingFieldChange(
                                                  selectedProfileKey,
                                                  "header.logoUrlTax",
                                                  e.target.value
                                                )
                                              }
                                              placeholder="https://..."
                                              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-opacity-20 transition-all"
                                            />
                                          </div>
                                          <div className="col-span-2">
                                            <label className="block text-xs font-medium text-gray-500 mb-2">
                                              Field Labels
                                            </label>
                                            <div className="grid grid-cols-3 gap-2">
                                              {[
                                                ["invoiceDate", "Invoice Date"],
                                                ["invoiceNo", "Invoice No."],
                                                ["projectNo", "Project No."],
                                                ["projectRef", "Project Ref"],
                                                ["orderedBy", "Ordered By"],
                                              ].map(([fieldKey, label]) => (
                                                <div key={fieldKey}>
                                                  <label className="block text-xs text-gray-400 mb-0.5">
                                                    {label}
                                                  </label>
                                                  <input
                                                    type="text"
                                                    value={
                                                      profile.invoiceLabels?.[
                                                        fieldKey
                                                      ] || ""
                                                    }
                                                    onChange={(e) =>
                                                      handleInvoicingFieldChange(
                                                        selectedProfileKey,
                                                        `invoiceLabels.${fieldKey}`,
                                                        e.target.value
                                                      )
                                                    }
                                                    className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-opacity-20 transition-all"
                                                  />
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Payment Section */}
                                      <div className="bg-gray-50/80 rounded-xl p-4">
                                        <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                          <svg
                                            className="w-4 h-4 opacity-60"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth={2}
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                                            />
                                          </svg>
                                          Payment Information
                                        </h5>
                                        <div className="grid grid-cols-2 gap-3">
                                          <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1">
                                              Section Heading
                                            </label>
                                            <input
                                              type="text"
                                              value={
                                                profile.payment?.heading || ""
                                              }
                                              onChange={(e) =>
                                                handleInvoicingFieldChange(
                                                  selectedProfileKey,
                                                  "payment.heading",
                                                  e.target.value
                                                )
                                              }
                                              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-opacity-20 transition-all"
                                            />
                                          </div>
                                          <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1">
                                              Account Holder
                                            </label>
                                            <input
                                              type="text"
                                              value={
                                                profile.payment
                                                  ?.accountHolder || ""
                                              }
                                              onChange={(e) =>
                                                handleInvoicingFieldChange(
                                                  selectedProfileKey,
                                                  "payment.accountHolder",
                                                  e.target.value
                                                )
                                              }
                                              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-opacity-20 transition-all"
                                            />
                                          </div>
                                          <div className="col-span-2">
                                            <label className="block text-xs font-medium text-gray-500 mb-1">
                                              Bank Details (one line per row)
                                            </label>
                                            <textarea
                                              value={(
                                                profile.payment?.bankLines || []
                                              ).join("\n")}
                                              onChange={(e) =>
                                                handleInvoicingListChange(
                                                  selectedProfileKey,
                                                  "payment",
                                                  "bankLines",
                                                  e.target.value
                                                )
                                              }
                                              rows={2}
                                              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-opacity-20 transition-all resize-none"
                                            />
                                          </div>
                                          <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1">
                                              Sort Code
                                            </label>
                                            <input
                                              type="text"
                                              value={
                                                profile.payment?.sortCode || ""
                                              }
                                              onChange={(e) =>
                                                handleInvoicingFieldChange(
                                                  selectedProfileKey,
                                                  "payment.sortCode",
                                                  e.target.value
                                                )
                                              }
                                              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-opacity-20 transition-all"
                                            />
                                          </div>
                                          <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1">
                                              Account Number
                                            </label>
                                            <input
                                              type="text"
                                              value={
                                                profile.payment?.accountNo || ""
                                              }
                                              onChange={(e) =>
                                                handleInvoicingFieldChange(
                                                  selectedProfileKey,
                                                  "payment.accountNo",
                                                  e.target.value
                                                )
                                              }
                                              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-opacity-20 transition-all"
                                            />
                                          </div>
                                          <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1">
                                              IBAN
                                            </label>
                                            <input
                                              type="text"
                                              value={
                                                profile.payment?.iban || ""
                                              }
                                              onChange={(e) =>
                                                handleInvoicingFieldChange(
                                                  selectedProfileKey,
                                                  "payment.iban",
                                                  e.target.value
                                                )
                                              }
                                              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-opacity-20 transition-all"
                                            />
                                          </div>
                                          <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1">
                                              SWIFT/BIC
                                            </label>
                                            <input
                                              type="text"
                                              value={
                                                profile.payment?.swift || ""
                                              }
                                              onChange={(e) =>
                                                handleInvoicingFieldChange(
                                                  selectedProfileKey,
                                                  "payment.swift",
                                                  e.target.value
                                                )
                                              }
                                              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-opacity-20 transition-all"
                                            />
                                          </div>
                                          <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1">
                                              Payment Terms
                                            </label>
                                            <input
                                              type="text"
                                              value={
                                                profile.payment?.terms || ""
                                              }
                                              onChange={(e) =>
                                                handleInvoicingFieldChange(
                                                  selectedProfileKey,
                                                  "payment.terms",
                                                  e.target.value
                                                )
                                              }
                                              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-opacity-20 transition-all"
                                            />
                                          </div>
                                          <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1">
                                              Additional Note
                                            </label>
                                            <input
                                              type="text"
                                              value={
                                                profile.payment?.note || ""
                                              }
                                              onChange={(e) =>
                                                handleInvoicingFieldChange(
                                                  selectedProfileKey,
                                                  "payment.note",
                                                  e.target.value
                                                )
                                              }
                                              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-opacity-20 transition-all"
                                            />
                                          </div>
                                        </div>
                                      </div>

                                      {/* Footer Section */}
                                      <div className="bg-gray-50/80 rounded-xl p-4">
                                        <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                          <svg
                                            className="w-4 h-4 opacity-60"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth={2}
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                                            />
                                          </svg>
                                          Footer
                                        </h5>
                                        <div className="grid grid-cols-2 gap-3">
                                          <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1">
                                              Footer Line
                                            </label>
                                            <textarea
                                              value={profile.footer?.line || ""}
                                              onChange={(e) =>
                                                handleInvoicingFieldChange(
                                                  selectedProfileKey,
                                                  "footer.line",
                                                  e.target.value
                                                )
                                              }
                                              rows={2}
                                              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-opacity-20 transition-all resize-none"
                                            />
                                          </div>
                                          <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1">
                                              Contact Info
                                            </label>
                                            <textarea
                                              value={
                                                profile.footer?.contact || ""
                                              }
                                              onChange={(e) =>
                                                handleInvoicingFieldChange(
                                                  selectedProfileKey,
                                                  "footer.contact",
                                                  e.target.value
                                                )
                                              }
                                              rows={2}
                                              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-opacity-20 transition-all resize-none"
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            </>
                          ) : (
                            <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                              <svg
                                className="w-12 h-12 mb-3"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={1}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                              <p className="text-sm">
                                Select a profile to configure
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Footer Actions */}
                        <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-gray-100">
                          <button
                            onClick={loadInvoicingConfig}
                            disabled={loadingInvoicing || invoicingSaving}
                            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                          >
                            <RefreshIcon />
                            Refresh
                          </button>
                          <button
                            onClick={saveInvoicingConfig}
                            disabled={invoicingSaving}
                            className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 shadow-sm"
                            style={{ backgroundColor: theme.filterAccent }}
                            onMouseEnter={(e) =>
                              (e.target.style.backgroundColor =
                                theme.filterAccentHover)
                            }
                            onMouseLeave={(e) =>
                              (e.target.style.backgroundColor =
                                theme.filterAccent)
                            }
                          >
                            {invoicingSaving ? (
                              <svg
                                className="animate-spin w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                />
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                              </svg>
                            ) : (
                              <CheckIcon />
                            )}
                            Save changes
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                        <svg
                          className="w-12 h-12 mb-3"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={1}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        <p className="text-sm">No invoicing config loaded</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default SettingsDialog;
